import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, FormGroup, Label, Input } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../Common/DataTable';
import { Breadcrumbs, H5 } from '../../../AbstractElements';
import { Btn } from '../../../AbstractElements';
import { Edit, UserPlus, UserX } from 'react-feather';
import { toast } from 'react-toastify';
import { fetchTableData, assignFinalCompensationToSelf, releaseFinalCompensationFromSelf } from '../../../Api/ApiMethods';
import { getFinalCompensationStatusLabel } from '../../../_helper/finalCompensationStatus';

const CompensationList = () => {
    const navigate = useNavigate();
    const isInitialLoad = useRef(true);
    const searchRef = useRef({
        customerSearch: '',
        licensePlateSearch: '',
        accidentDateSearch: '',
        paymentDateSearch: ''
    });

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [customerSearch, setCustomerSearch] = useState('');
    const [licensePlateSearch, setLicensePlateSearch] = useState('');
    const [accidentDateSearch, setAccidentDateSearch] = useState('');
    const [paymentDateSearch, setPaymentDateSearch] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);
    const [isReleasing, setIsReleasing] = useState(false);

    const apiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/FinalCompensation';

    useEffect(() => {
        searchRef.current = {
            customerSearch,
            licensePlateSearch,
            accidentDateSearch,
            paymentDateSearch
        };
    }, [customerSearch, licensePlateSearch, accidentDateSearch, paymentDateSearch]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            const {
                customerSearch: currentCustomerSearch,
                licensePlateSearch: currentLicensePlateSearch,
                accidentDateSearch: currentAccidentDateSearch,
                paymentDateSearch: currentPaymentDateSearch
            } = searchRef.current;

            if (currentCustomerSearch && currentCustomerSearch.trim().length >= 2) {
                queryParams.append('customer', currentCustomerSearch.trim());
            }
            if (currentLicensePlateSearch && currentLicensePlateSearch.trim().length >= 2) {
                queryParams.append('licensePlate', currentLicensePlateSearch.trim());
            }
            if (currentAccidentDateSearch) {
                queryParams.append('accidentDate', currentAccidentDateSearch);
            }
            if (currentPaymentDateSearch) {
                queryParams.append('paymentDate', currentPaymentDateSearch);
            }

            const queryString = queryParams.toString();
            const endpoint = queryString ? `${apiUrl}?${queryString}` : apiUrl;

            await fetchTableData({
                apiEndpoint: endpoint,
                page,
                pageSize,
                setDataCallback: (rows) => {
                    setItems(Array.isArray(rows) ? rows : []);
                },
                setTotalRowsCallback: setTotalRows,
            });
        } catch {
            toast.error('Kapak verileri yüklenirken hata oluştu.');
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, apiUrl]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        const timeoutId = setTimeout(() => {
            const hasCustomer = customerSearch && customerSearch.trim().length >= 2;
            const hasPlate = licensePlateSearch && licensePlateSearch.trim().length >= 2;
            const hasDate = accidentDateSearch.trim();
            const hasPaymentDate = paymentDateSearch.trim();

            if (hasCustomer || hasPlate || hasDate || hasPaymentDate || (!customerSearch && !licensePlateSearch && !accidentDateSearch && !paymentDateSearch)) {
                fetchData();
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [customerSearch, licensePlateSearch, accidentDateSearch, paymentDateSearch, fetchData]);

    const handleAssignToSelf = async (compensationId) => {
        if (isAssigning) return;
        setIsAssigning(true);
        try {
            await assignFinalCompensationToSelf({ finalCompensationId: compensationId });
            toast.success('Kapak kendinize atandı.');
            fetchData();
        } catch { }
        finally { setIsAssigning(false); }
    };

    const handleReleaseFromSelf = async (compensationId) => {
        if (isReleasing) return;
        setIsReleasing(true);
        try {
            await releaseFinalCompensationFromSelf({ finalCompensationId: compensationId });
            toast.success('Kapak görevi kaldırıldı.');
            fetchData();
        } catch { }
        finally { setIsReleasing(false); }
    };

    const formatCurrency = (value) => {
        if (value == null) return '-';
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
    };

    const formatDate = (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString('tr-TR');
    };

    const columns = [
        {
            name: 'Müşteri',
            selector: row => row.customerName || '-',
            sortable: true,
            grow: 2,
            minWidth: '180px'
        },
        {
            name: 'Plaka',
            selector: row => row.licensePlate || '-',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Kaza Tarihi',
            selector: row => formatDate(row.accidentDate),
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'İcra Dosya No',
            selector: row => row.enforcementCaseNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'Tahsilat Tutarı',
            selector: row => {
                const pc = row.paymentCollections;
                const amount = pc?.amount || row.collectedAmount;
                return formatCurrency(amount);
            },
            sortable: true,
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'Ödeme Tarihi',
            selector: row => formatDate(row.paymentDate || row.paymentCollections?.date),
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Kapak Durumu',
            selector: row => getFinalCompensationStatusLabel(row) || '-',
            sortable: true,
            grow: 1,
            minWidth: '180px'
        },
        {
            name: 'Ata / Kaldır',
            cell: (row) => {
                const appointedUser = row.appointedUser || row.appointedUsers;
                const hasAppointedUser = appointedUser && (
                    Array.isArray(appointedUser)
                        ? appointedUser.filter(u => !!u && String(u).trim() !== '').length > 0
                        : !!appointedUser
                );

                if (!hasAppointedUser) {
                    return (
                        <Btn attrBtn={{ color: 'outline-primary', size: 'sm', onClick: () => handleAssignToSelf(row.id), title: 'Kendine Ata', disabled: isAssigning }}>
                            <UserPlus size={16} />
                        </Btn>
                    );
                }

                return (
                    <Btn attrBtn={{ color: 'outline-danger', size: 'sm', onClick: () => handleReleaseFromSelf(row.id), title: 'Kendinden Kaldır', disabled: isReleasing }}>
                        <UserX size={16} />
                    </Btn>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '110px',
            center: true
        },
        {
            name: 'Detay',
            cell: (row) => (
                <Btn
                    attrBtn={{
                        color: 'outline-primary',
                        size: 'sm',
                        onClick: () => navigate(`${process.env.PUBLIC_URL}/compensation-detail/${row.id}`),
                        title: 'Kapak Detayı'
                    }}
                >
                    <Edit size={16} />
                </Btn>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '80px',
            center: true
        },
    ];

    return (
        <>
            <Breadcrumbs parent="Müşteri Alanları" title="Kapaklar" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <H5>Kapaklar</H5>
                                <span>Oluşturulmuş kapak kayıtları aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <Row className="mb-3">
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Müşteri Adı</Label>
                                            <Input
                                                type="text"
                                                placeholder="Müşteri adı... (en az 2 karakter)"
                                                value={customerSearch}
                                                onChange={(e) => { setCustomerSearch(e.target.value); setPage(1); }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Plaka</Label>
                                            <Input
                                                type="text"
                                                placeholder="Plaka... (en az 2 karakter)"
                                                value={licensePlateSearch}
                                                onChange={(e) => { setLicensePlateSearch(e.target.value); setPage(1); }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Kaza Tarihi</Label>
                                            <Input
                                                type="date"
                                                value={accidentDateSearch}
                                                onChange={(e) => { setAccidentDateSearch(e.target.value); setPage(1); }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>Ödeme Tarihi</Label>
                                            <Input
                                                type="date"
                                                value={paymentDateSearch}
                                                onChange={(e) => { setPaymentDateSearch(e.target.value); setPage(1); }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <DataTable
                                    columns={columns}
                                    data={items}
                                    progressPending={loading}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    onChangePage={setPage}
                                    onChangeRowsPerPage={(newPageSize, newPage) => { setPageSize(newPageSize); setPage(newPage); }}
                                    paginationPerPage={pageSize}
                                    paginationDefaultPage={page}
                                    selectableRows={false}
                                    progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                    noDataComponent={loading ? null : 'Kapak kaydı bulunamadı'}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default CompensationList;
